const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const debugEnabled =
  new URLSearchParams(window.location.search).has('chemilab_motion_debug') ||
  window.localStorage.getItem('chemilab_motion_debug') === 'true';

window.ChemilabMotion = {
  version: '1.1.0',
  debug: debugEnabled,
  reducedMotion: prefersReducedMotion.matches,
  loaded: false,
  errors: [],
  counts: {},
  events: [],
  inspect,
  test: runManualMotionTest,
};

if (!prefersReducedMotion.matches) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initChemilabMotion, { once: true });
  } else {
    initChemilabMotion();
  }
} else {
  recordMotionEvent('Motion skipped: prefers-reduced-motion is enabled.');
}

function initChemilabMotion() {
  const motion = window.Motion;

  if (!motion?.animate || !motion?.inView || !motion?.stagger) {
    const message = 'Chemilab Motion animations could not start because Motion is not available.';
    window.ChemilabMotion.errors.push(message);
    console.warn(message);
    return;
  }

  window.ChemilabMotion.loaded = true;
  recordMotionEvent('Motion loaded and initialized.');
  initMotionInScope(document, motion);

  document.addEventListener('shopify:section:load', (event) => {
    recordMotionEvent('Shopify section reloaded.', event.target?.id || event.target?.className || 'unknown');
    initMotionInScope(event.target, motion);
  });
}

function initMotionInScope(scope, motion) {
  initScrollReveals(scope, motion);
  initSlideshows(scope, motion);
  initScrollableCarousels(scope, motion);
}

function initScrollReveals(scope, { animate, inView, stagger }) {
  const groups = [
    {
      root: '.brand-logo-cloud',
      items: '.brand-logo-cloud__item',
      from: { opacity: 0, y: 22, filter: 'blur(8px)' },
      to: { opacity: 1, y: 0, filter: 'blur(0px)' },
      delay: 0.045,
    },
    {
      root: '.multicolumn',
      items: '.multicolumn-card',
      from: { opacity: 0, y: 26, scale: 0.98 },
      to: { opacity: 1, y: 0, scale: 1 },
      delay: 0.07,
    },
    {
      root: '.collection',
      items: '.product-card-wrapper',
      from: { opacity: 0, y: 24, scale: 0.985 },
      to: { opacity: 1, y: 0, scale: 1 },
      delay: 0.045,
    },
    {
      root: '.chemilab-routed-page',
      items:
        '.chemilab-routed-page__copy, .chemilab-routed-page__visual, .chemilab-routed-page__stats article, .chemilab-routed-page__story > *, .chemilab-routed-page__cards article, .chemilab-routed-page__form-copy, .chemilab-routed-page__form',
      from: { opacity: 0, y: 18, scale: 0.99 },
      to: { opacity: 1, y: 0, scale: 1 },
      delay: 0.055,
    },
    {
      root: '.chemilab-page-hero',
      items: '.chemilab-page-hero__content, .chemilab-page-hero__media, .chemilab-page-hero__stat',
      from: { opacity: 0, y: 18, scale: 0.99 },
      to: { opacity: 1, y: 0, scale: 1 },
      delay: 0.055,
    },
    {
      root: '.chemilab-contact-panel',
      items: '.chemilab-contact-panel__intro, .chemilab-contact-panel__card',
      from: { opacity: 0, y: 18, scale: 0.99 },
      to: { opacity: 1, y: 0, scale: 1 },
      delay: 0.055,
    },
  ];

  groups.forEach(({ root, items, from, to, delay }) => {
    scope.querySelectorAll(root).forEach((section) => {
      if (section.dataset.chemilabRevealReady === 'true') return;
      section.dataset.chemilabRevealReady = 'true';

      const targets = Array.from(section.querySelectorAll(items));
      if (!targets.length) return;

      recordMotionCount(root, targets.length);
      recordMotionEvent('Reveal group prepared.', `${root}: ${targets.length} items`);
      animate(targets, from, { duration: 0.01 });

      inView(
        section,
        () => {
          recordMotionEvent('Reveal group entered viewport.', root);
          animate(targets, to, {
            duration: 0.58,
            delay: stagger(delay),
            easing: [0.16, 1, 0.3, 1],
          });
        },
        { amount: 0.18, margin: '0px 0px -8% 0px' }
      );

      if (isElementInViewport(section)) {
        window.setTimeout(() => {
          recordMotionEvent('Reveal group already in viewport.', root);
          animate(targets, to, {
            duration: 0.58,
            delay: stagger(delay),
            easing: [0.16, 1, 0.3, 1],
          });
        }, 120);
      }
    });
  });
}

function initSlideshows(scope, { animate }) {
  scope.querySelectorAll('slideshow-component').forEach((slideshow) => {
    if (slideshow.dataset.chemilabSlideshowMotion === 'true') return;
    if (slideshow.querySelector('.announcement-bar-slider')) return;

    const slider = slideshow.querySelector('.slideshow');
    if (!slider) return;

    slideshow.dataset.chemilabSlideshowMotion = 'true';
    recordMotionEvent('Slideshow prepared.', slideshow.id || slideshow.className || 'slideshow-component');

    const animateActiveSlide = () => {
      const activeSlide =
        slideshow.querySelector('.slideshow__slide[aria-hidden="false"]') ||
        slideshow.querySelector('.slideshow__slide');

      if (!activeSlide || activeSlide.dataset.chemilabSlideAnimated === 'true') return;

      slideshow.querySelectorAll('.slideshow__slide').forEach((slide) => {
        slide.dataset.chemilabSlideAnimated = 'false';
      });

      activeSlide.dataset.chemilabSlideAnimated = 'true';
      recordMotionEvent('Slideshow slide animated.', activeSlide.id || 'active slide');

      const media = activeSlide.querySelector('.slideshow__media');
      const content = activeSlide.querySelector('.slideshow__text');
      const contentChildren = activeSlide.querySelectorAll(
        '.banner__heading, .banner__text, .banner__buttons'
      );

      if (media) {
        animate(
          media,
          { opacity: [0.78, 1], scale: [1.035, 1] },
          { duration: 0.92, easing: [0.16, 1, 0.3, 1] }
        );
      }

      if (content) {
        animate(
          content,
          { opacity: [0, 1], y: [24, 0] },
          { duration: 0.62, easing: [0.16, 1, 0.3, 1] }
        );
      }

      if (contentChildren.length) {
        animate(
          contentChildren,
          { opacity: [0, 1], y: [18, 0] },
          { duration: 0.54, delay: stagger(0.075), easing: [0.16, 1, 0.3, 1] }
        );
      }
    };

    let scrollFrame;
    const requestSlideAnimation = () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(animateActiveSlide);
    };

    requestSlideAnimation();
    slider.addEventListener('scroll', requestSlideAnimation, { passive: true });

    slideshow.querySelectorAll('.slider-button, .slider-counter__link').forEach((control) => {
      control.addEventListener('click', () => {
        window.setTimeout(requestSlideAnimation, 80);
      });
    });
  });
}

function initScrollableCarousels(scope, { animate }) {
  const carousels = scope.querySelectorAll('.brand-logo-cloud__carousel, slider-component');

  carousels.forEach((carousel) => {
    if (carousel.dataset.chemilabCarouselMotion === 'true') return;
    carousel.dataset.chemilabCarouselMotion = 'true';

    const scroller = carousel.matches('slider-component')
      ? carousel.querySelector('.slider')
      : carousel;

    if (!scroller) return;
    recordMotionEvent('Carousel prepared.', carousel.id || carousel.className || 'carousel');

    let animationFrame;

    const animateVisibleItems = () => {
      const items = Array.from(
        carousel.querySelectorAll('.brand-logo-cloud__item, .product-card-wrapper, .multicolumn-card')
      );

      const visibleItems = items.filter((item) => isElementMostlyVisible(item, scroller));
      if (!visibleItems.length) return;

      recordMotionEvent('Carousel visible items animated.', visibleItems.length);
      animate(
        visibleItems,
        { opacity: [0.82, 1], y: [10, 0], scale: [0.985, 1] },
        { duration: 0.36, easing: [0.16, 1, 0.3, 1] }
      );
    };

    const requestCarouselAnimation = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(animateVisibleItems);
    };

    carousel.querySelectorAll('.slider-button').forEach((button) => {
      button.addEventListener('click', () => window.setTimeout(requestCarouselAnimation, 120));
    });

    scroller.addEventListener('scroll', requestCarouselAnimation, { passive: true });
  });
}

function isElementMostlyVisible(element, root) {
  const elementRect = element.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const visibleWidth = Math.min(elementRect.right, rootRect.right) - Math.max(elementRect.left, rootRect.left);
  const visibleHeight = Math.min(elementRect.bottom, rootRect.bottom) - Math.max(elementRect.top, rootRect.top);

  return visibleWidth > elementRect.width * 0.35 && visibleHeight > elementRect.height * 0.35;
}

function isElementInViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
}

function inspect() {
  const report = {
    motionGlobal: Boolean(window.Motion),
    motionMethods: {
      animate: Boolean(window.Motion?.animate),
      inView: Boolean(window.Motion?.inView),
      stagger: Boolean(window.Motion?.stagger),
    },
    reducedMotion: prefersReducedMotion.matches,
    loaded: window.ChemilabMotion.loaded,
    template: document.querySelector('main[data-template]')?.dataset.template,
    counts: {
      slideshows: document.querySelectorAll('slideshow-component').length,
      slideshowSlides: document.querySelectorAll('.slideshow__slide').length,
      productCards: document.querySelectorAll('.product-card-wrapper').length,
      brandCards: document.querySelectorAll('.brand-logo-cloud__item').length,
      multicolumnCards: document.querySelectorAll('.multicolumn-card').length,
      routedPageItems: document.querySelectorAll('.chemilab-routed-page__visual, .chemilab-routed-page__cards article').length,
    },
    preparedCounts: window.ChemilabMotion.counts,
    errors: window.ChemilabMotion.errors,
    recentEvents: window.ChemilabMotion.events.slice(-20),
  };

  console.table(report.counts);
  console.info('Chemilab Motion inspect:', report);
  return report;
}

function runManualMotionTest() {
  const motion = window.Motion;
  if (!motion?.animate) {
    console.warn('Motion is not available. Run ChemilabMotion.inspect() for details.');
    return false;
  }

  const targets = document.querySelectorAll(
    '.slideshow__text, .product-card-wrapper, .brand-logo-cloud__item, .multicolumn-card, .chemilab-routed-page__visual, .chemilab-routed-page__cards article, .chemilab-page-hero__media'
  );

  if (!targets.length) {
    console.warn('No Chemilab motion targets were found on this page.');
    return false;
  }

  motion.animate(
    targets,
    { opacity: [0.35, 1], y: [28, 0], scale: [0.96, 1] },
    { duration: 0.8, delay: motion.stagger(0.035), easing: [0.16, 1, 0.3, 1] }
  );

  recordMotionEvent('Manual motion test executed.', targets.length);
  return true;
}

function recordMotionCount(key, count) {
  window.ChemilabMotion.counts[key] = (window.ChemilabMotion.counts[key] || 0) + count;
}

function recordMotionEvent(message, detail) {
  const entry = {
    time: new Date().toISOString(),
    message,
    detail,
  };

  window.ChemilabMotion.events.push(entry);

  if (debugEnabled) {
    console.info('[Chemilab Motion]', message, detail || '');
  }
}
